import React, { useState, useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { imageUrl } from '../lib/images'

const DEFAULT_NAV = [
  { titel: 'Forside', sti: '/', sortering: 1 },
  { titel: 'Om Unges Fremtid', sti: '/om-unges-fremtid', sortering: 2 },
  { titel: 'Vores arbejde', sti: '/vores-arbejde', sortering: 3 },
  { titel: 'Børn og unge', sti: '/for-boern-og-unge', sortering: 4 },
  { titel: 'Støt os', sti: '/bliv-involveret', sortering: 5 },
  { titel: 'Nyheder', sti: '/nyheder', sortering: 6 },
  { titel: 'Kontakt', sti: '/kontakt', sortering: 7 }
]

export default function Navbar() {
  const [navItems, setNavItems] = useState(DEFAULT_NAV)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  const location = useLocation()

  // Hent menupunkter fra CMS (filtrerer galleri og inaktive fra)
  useEffect(() => {
    fetch('/_api/public/content/navigation')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente navigation')
        return res.json()
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const filtered = data
            .filter((item) => item.sti !== '/galleri' && item.vis_i_menu !== false)
            .sort((a, b) => (Number(a.sortering) || 0) - (Number(b.sortering) || 0))
          if (filtered.length > 0) {
            setNavItems(filtered)
          }
        }
      })
      .catch(() => {
        // Fallback til DEFAULT_NAV
      })
  }, [])

  // Robust hysteresis for sticky header og logo for at eliminere enhver rysten/jumpen
  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentY = window.scrollY
          setIsScrolled((prev) => {
            if (!prev && currentY > 48) return true
            if (prev && currentY < 16) return false
            return prev
          })
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Luk mobilmenu ved ruteskift
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname, location.hash])

  // Forhindr baggrundsscroll når mobilmenu er åben
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  return (
    <header className="sticky top-0 z-40 bg-white transition-all duration-300 ease-in-out">
      {/* Top utility bar */}
      <div className="bg-[#0c304d] text-white text-xs py-2 px-4 sm:px-6 lg:px-8 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#f39223] text-white shadow-2xs">
              Nonprofit
            </span>
            <span className="text-neutral-200 hidden sm:inline">
              Vi støtter og styrker udsatte børn og unge
            </span>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            <a
              href="tel:25778646"
              className="inline-flex items-center gap-1.5 text-neutral-200 hover:text-white transition-colors cursor-pointer"
            >
              <i className="fa-solid fa-phone text-[#f39223]" aria-hidden="true" />
              <span className="font-semibold whitespace-nowrap">25 77 86 46</span>
            </a>
            <a
              href="mailto:ungesfremtid@outlook.dk"
              className="hidden md:inline-flex items-center gap-1.5 text-neutral-200 hover:text-white transition-colors cursor-pointer"
            >
              <i className="fa-solid fa-envelope text-[#f39223]" aria-hidden="true" />
              <span>ungesfremtid@outlook.dk</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div
        className={`w-full border-b transition-all duration-300 ease-in-out ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md border-neutral-200/90 shadow-md'
            : 'bg-white border-neutral-200/80 shadow-xs'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`flex items-center justify-between transition-all duration-300 ease-in-out ${
              isScrolled ? 'h-18 sm:h-20' : 'h-24 sm:h-26'
            }`}
          >
            {/* Brand Logo */}
            <Link
              to="/"
              className="flex items-center gap-3 group focus:outline-hidden focus:ring-2 focus:ring-[#0c304d] rounded-lg p-1"
              aria-label="Unges Fremtid - Forside"
            >
              <img
                src={imageUrl('/_assets/unges-fremtid-logo.png', 'width=480,quality=95,format=auto')}
                alt="Unges Fremtid Logo"
                className={`w-auto object-contain transition-all duration-300 ease-in-out transform-gpu origin-left ${
                  isScrolled ? 'h-12 sm:h-14' : 'h-18 sm:h-20'
                }`}
                width={220}
                height={75}
              />
            </Link>

            {/* Desktop Navigation - vises kun ved skærmbredder over eller lig med 1200px (uden dropdowns og uden særskilt "Kontakt" link) */}
            <nav className="hidden min-[1200px]:flex items-center gap-1" aria-label="Hovednavigation">
              {navItems
                .filter((item) => item.sti !== '/kontakt' && item.sti !== '/galleri' && item.vis_i_menu !== false)
                .map((item) => (
                  <NavLink
                    key={item.sti}
                    to={item.sti}
                    end={item.sti === '/'}
                    className={({ isActive }) =>
                      `px-3.5 py-2 rounded-lg text-[15px] font-medium transition-all duration-150 relative inline-flex items-center ${
                        isActive
                          ? 'text-[#0c304d] font-semibold bg-neutral-100'
                          : 'text-neutral-700 hover:text-[#0c304d] hover:bg-neutral-50'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span>{item.titel}</span>
                        {isActive && (
                          <span className="absolute bottom-1 left-3.5 right-3.5 h-0.5 bg-[#f39223] rounded-full" />
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
            </nav>

            {/* Right Action Button (Desktop min-1200px) */}
            <div className="hidden min-[1200px]:flex items-center gap-3">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 bg-[#f39223] hover:bg-[#e08112] text-white font-semibold text-sm px-5 py-2.5 rounded-full shadow-xs hover:shadow-md transition-all duration-200 cursor-pointer"
              >
                <span>Kontakt os</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>

            {/* Burger Menu Button - synlig under 1200px */}
            <div className="flex min-[1200px]:hidden items-center gap-2">
              <a
                href="tel:25778646"
                className="inline-flex items-center justify-center p-2 rounded-lg text-[#0c304d] bg-amber-50 hover:bg-amber-100 transition-colors"
                aria-label="Ring til os på 25 77 86 46"
              >
                <i className="fa-solid fa-phone text-[#f39223]" aria-hidden="true" />
              </a>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex items-center justify-center p-2.5 rounded-lg text-neutral-800 hover:text-[#0c304d] hover:bg-neutral-100 focus:outline-hidden focus:ring-2 focus:ring-[#0c304d] transition-colors"
                aria-expanded={mobileMenuOpen}
                aria-label={mobileMenuOpen ? 'Luk menu' : 'Åbn menu'}
              >
                <i
                  className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Drawer Navigation (Sidebar) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden fixed inset-0 z-50">
          {/* Mørk baggrunds-overlay */}
          <div
            className="fixed inset-0 bg-[#0c304d]/60 backdrop-blur-xs transition-opacity duration-300"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />

          {/* Uafhængigt scrollbart sidebar-panel */}
          <div className="fixed top-0 right-0 bottom-0 w-full max-w-sm sm:max-w-md bg-white shadow-2xl flex flex-col h-[100dvh] max-h-[100dvh] z-50 overflow-hidden">
            {/* Fast Top Bar */}
            <div className="shrink-0 p-4 sm:p-5 border-b border-neutral-200 flex items-center justify-between bg-white">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2"
                aria-label="Unges Fremtid - Forside"
              >
                <img
                  src={imageUrl('/_assets/unges-fremtid-logo.png', 'width=280,quality=90,format=auto')}
                  alt="Unges Fremtid Logo"
                  className="h-11 w-auto object-contain"
                  width={140}
                  height={44}
                />
              </Link>

              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="p-2.5 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-800 transition-colors"
                aria-label="Luk menu"
              >
                <i className="fa-solid fa-xmark text-xl" aria-hidden="true" />
              </button>
            </div>

            {/* Scrollable menuindhold */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain overflow-x-hidden p-4 sm:p-6 space-y-2">
              <span className="block text-xs font-bold uppercase tracking-wider text-neutral-400 px-3 py-1 mb-1">
                Navigation
              </span>

              {navItems
                .filter((item) => item.sti !== '/kontakt' && item.sti !== '/galleri' && item.vis_i_menu !== false)
                .map((item) => (
                  <NavLink
                    key={item.sti}
                    to={item.sti}
                    end={item.sti === '/'}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center justify-between px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                        isActive
                          ? 'bg-amber-50 text-[#0c304d] font-bold border-l-4 border-[#f39223]'
                          : 'text-neutral-800 hover:bg-neutral-50'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span>{item.titel}</span>
                        <i
                          className={`fa-solid ${
                            isActive ? 'fa-arrow-right text-[#f39223]' : 'fa-angle-right text-neutral-300'
                          }`}
                          aria-hidden="true"
                        />
                      </>
                    )}
                  </NavLink>
                ))}

              {/* Fremhævet "Kontakt os" knap i selve sidebar-menuen */}
              <div className="pt-3 px-1">
                <Link
                  to="/kontakt"
                  onClick={() => setMobileMenuOpen(false)}
                  className="inline-flex w-full items-center justify-center gap-2 bg-[#f39223] hover:bg-[#e08112] text-white font-semibold text-sm px-5 py-3 rounded-full shadow-xs hover:shadow-md transition-all duration-200 cursor-pointer"
                >
                  <span>Kontakt os</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Fast Bund med Hurtig kontakt */}
            <div className="shrink-0 p-4 sm:p-6 border-t border-neutral-200 bg-neutral-50 space-y-3.5">
              <div>
                <p className="text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">
                  Hurtig kontakt
                </p>
                <div className="space-y-2 text-sm">
                  <a
                    href="tel:25778646"
                    className="flex items-center gap-3 text-[#0c304d] font-bold text-base hover:text-[#f39223]"
                  >
                    <div className="w-8 h-8 rounded-full bg-amber-100 text-[#f39223] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                    </div>
                    <span className="whitespace-nowrap">25 77 86 46</span>
                  </a>
                  <a
                    href="mailto:ungesfremtid@outlook.dk"
                    className="flex items-center gap-3 text-neutral-700 hover:text-[#0c304d]"
                  >
                    <div className="w-8 h-8 rounded-full bg-white border border-neutral-200 text-[#f39223] flex items-center justify-center shrink-0">
                      <i className="fa-solid fa-envelope text-xs" aria-hidden="true" />
                    </div>
                    <span className="break-all text-xs sm:text-sm">ungesfremtid@outlook.dk</span>
                  </a>
                </div>
              </div>

              <Link
                to="/kontakt"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0c304d] hover:bg-[#134267] text-white font-bold text-sm py-3 px-6 rounded-full shadow-xs transition-colors"
              >
                <span>Skriv til os</span>
                <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
